import React, { Fragment, useMemo } from 'react';

import { track } from '@src/services/analytics';
import { useWidgetId } from '@src/core/IdProvider';
import Calendar from '@src/components/reusable/Calendar';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useScheduleDatesLoader } from '@src/domains/scheduling';
import {
  productSelector,
  selectAvailableDate,
  tariffsState,
  adderResumeSelector,
  goToNextStep,
  productPackageProductsSelector,
} from '@src/stores/product';
import { appInfoSelector } from '@src/stores/app';
import MultiProductCalendarPreviousStepButton from '@src/components/ProductPackage/ProductPackageCalendarPreviousStepButton';
import useBreakpoint from '@src/hooks/useBreakpoint';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import { useIsSingleBookingPackageBooking, usePackageScheduleIntersection } from '@src/features/productPackage';
import { SellingMode } from '@src/core/buyerApiClient';

interface ProductAdderCalendarProps {
  isMultiProductFlow?: boolean;
}

const ProductAdderCalendar = (props: Readonly<ProductAdderCalendarProps>) => {
  const { isMultiProductFlow = false } = props;
  const widgetId = useWidgetId();
  const { productId } = useAppSelector(adderResumeSelector(widgetId));

  const product = useAppSelector(productSelector(productId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const dispatch = useAppDispatch();
  const appInfo = useAppSelector(appInfoSelector);

  const { isDesktop } = useBreakpoint();

  const seedDate = useMemo(() => (selectedDate ? new Date(selectedDate) : null), [selectedDate]);
  const isSingleBooking = useIsSingleBookingPackageBooking();
  const packageId = usePackageIdParam();
  const packageProducts = useAppSelector(productPackageProductsSelector(packageId ?? ''));

  const scheduledProductIds = useMemo(
    () =>
      (packageProducts ?? [])
        .filter((item) => item.product?.sellingMode && item.product.sellingMode !== SellingMode.WithoutDate)
        .map((item) => item.productId),
    [packageProducts],
  );

  const packageSchedule = usePackageScheduleIntersection({
    scheduledProductIds,
    enabled: isSingleBooking,
  });

  const productSchedule = useScheduleDatesLoader({ productId, seedDate, enabled: !isSingleBooking });
  const { monthTuple, setMonthTuple, dates, pending } = isSingleBooking ? packageSchedule : productSchedule;

  const handleDateSelect = (date: string) => {
    track('productAdderDateSelected', {
      productId: product?.id ?? '',
      productName: product?.name ?? '',
      date,
    });

    dispatch(selectAvailableDate({ id: widgetId, value: date, isSingleBookingPackageBooking: isSingleBooking }));
    dispatch(goToNextStep(widgetId));
  };

  return (
    <Fragment>
      <Calendar
        dates={dates}
        pending={pending}
        monthTuple={monthTuple}
        onMonthTupleChange={setMonthTuple}
        onDateSelect={handleDateSelect}
        tariffToShowThePrice={appInfo?.featuredTariffPreviewMode}
      />

      {isMultiProductFlow && !isDesktop ? <MultiProductCalendarPreviousStepButton /> : null}
    </Fragment>
  );
};

export default ProductAdderCalendar;
